/**
 * SVG splitting utilities for OpenSCAD import compatibility.
 *
 * Splits a complex SVG into individual per-shape SVG files. Each top-level
 * child element of the source <svg> becomes its own standalone SVG that
 * preserves the original viewBox so coordinates remain consistent with
 * the parent when imported into OpenSCAD.
 *
 * Known limitations:
 * - <defs> (gradients, clip paths, symbols) are not copied into split files.
 *   Shapes that reference <defs> entries may render differently in isolation.
 * - Only direct children of the root <svg> are split; nested sub-groups are
 *   kept intact within their parent shape file.
 * - Text elements are preserved as-is but require the relevant font to be
 *   available in the OpenSCAD environment.
 */

// Tags that carry metadata or shared resources — not geometry, skip them.
const SKIP_TAGS = new Set(['defs', 'title', 'desc', 'metadata', 'style']);

export interface SplitSvgFile {
  filename: string;
  content: string;
  size: number;
}

export interface SplitSvgResult {
  files: SplitSvgFile[];
  /** Zero-padded width used in filenames, e.g. 2 for "01", 3 for "001" */
  padWidth: number;
  baseName: string;
}

/**
 * Derive a safe base name from the original SVG filename.
 * Strips the .svg extension and replaces non-alphanumeric chars with underscores.
 */
function getBaseName(filename: string): string {
  const withoutExt = filename.replace(/\.svg$/i, '');
  return (
    withoutExt
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'shape'
  );
}

/**
 * Collect SVG root attributes that should be forwarded to every split file.
 * This includes namespace declarations, viewBox, width, height, and
 * presentation attributes (fill, stroke, etc.) that act as defaults.
 */
function getRootAttributes(svgEl: SVGSVGElement): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (let i = 0; i < svgEl.attributes.length; i++) {
    const attr = svgEl.attributes[i];
    attrs[attr.name] = attr.value;
  }
  return attrs;
}

/**
 * Serialize a set of attributes into an XML attribute string.
 */
function serializeAttrs(attrs: Record<string, string>): string {
  return Object.entries(attrs)
    .map(([k, v]) => `${k}="${escapeXmlAttr(v)}"`)
    .join(' ');
}

function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Build a standalone SVG string containing a single child element.
 * The root attributes (including viewBox) are preserved so the shape
 * appears at the correct position within the coordinate space.
 */
function buildSingleShapeSvg(
  rootAttrs: Record<string, string>,
  childElement: Element,
): string {
  const childXml = childElement.outerHTML;
  const attrsStr = serializeAttrs(rootAttrs);
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg ${attrsStr}>
${childXml}
</svg>
`;
}

/**
 * Split an SVG string into individual per-shape SVG files.
 *
 * @param svgContent  Raw SVG text content
 * @param filename    Original filename (used to derive split file names)
 * @returns           Array of split files with generated filenames and content
 * @throws            If the SVG cannot be parsed or contains no splittable shapes
 */
export function splitSvg(svgContent: string, filename: string): SplitSvgResult {
  const { svgEl } = parseSvgDoc(svgContent);
  const rootAttrs = getRootAttributes(svgEl);

  // Collect geometry children (skip metadata/defs/style tags)
  const geometryChildren: Element[] = [];
  for (let i = 0; i < svgEl.children.length; i++) {
    const child = svgEl.children[i];
    const tag = child.tagName.toLowerCase().replace(/^svg:/, '');
    if (!SKIP_TAGS.has(tag)) {
      geometryChildren.push(child);
    }
  }

  if (geometryChildren.length === 0) {
    throw new Error(
      'No splittable shapes found in SVG (only metadata/defs/style elements present)',
    );
  }

  if (geometryChildren.length === 1) {
    throw new Error('SVG contains only one shape — nothing to split');
  }

  const baseName = getBaseName(filename);
  const padWidth = Math.max(2, String(geometryChildren.length).length);

  const files: SplitSvgFile[] = geometryChildren.map((child, index) => {
    const suffix = String(index + 1).padStart(padWidth, '0');
    const splitFilename = `${baseName}_${suffix}.svg`;
    const content = buildSingleShapeSvg(rootAttrs, child);
    return {
      filename: splitFilename,
      content,
      size: new TextEncoder().encode(content).length,
    };
  });

  return { files, padWidth, baseName };
}

/**
 * Parse an SVG string and return the file without parse errors, throwing if
 * the content is invalid. Shared by fragment helpers below.
 */
function parseSvgDoc(svgContent: string): {
  doc: Document;
  svgEl: SVGSVGElement;
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error(
      `Failed to parse SVG: ${parseError.textContent?.trim() ?? 'unknown error'}`,
    );
  }
  const svgEl = doc.documentElement as unknown as SVGSVGElement;
  if (svgEl.tagName.toLowerCase() !== 'svg') {
    throw new Error('Root element is not an <svg> element');
  }
  return { doc, svgEl };
}

/**
 * Extract a single element by ID from an SVG and return it as a standalone
 * SVG string, preserving the root viewBox and presentation attributes so that
 * coordinates remain consistent with the parent document.
 *
 * This is used to implement `import("file.svg#id")` — the fragment form of
 * the OpenSCAD import() call that lets users import one named shape from a
 * multi-shape SVG without first splitting the file.
 *
 * @param svgContent  Raw SVG text content
 * @param id          The value of the `id` attribute to look for
 * @returns           A complete, standalone SVG string containing just that element
 * @throws            If the SVG cannot be parsed or no element with the given ID exists
 */
export function extractSvgFragment(svgContent: string, id: string): string {
  const { svgEl } = parseSvgDoc(svgContent);
  const rootAttrs = getRootAttributes(svgEl);

  // Search the entire document (any nesting level) for the element with this id.
  const el = svgEl.querySelector(`[id="${CSS.escape(id)}"]`);
  if (!el) {
    throw new Error(
      `SVG has no element with id="${id}" — cannot import fragment`,
    );
  }

  return buildSingleShapeSvg(rootAttrs, el);
}

/**
 * Return all element IDs found anywhere in the SVG document (at any depth).
 * The list is returned in document order and deduplicated.
 *
 * @param svgContent  Raw SVG text content
 * @returns           Ordered, unique list of id attribute values
 */
export function getSvgIds(svgContent: string): string[] {
  const { svgEl } = parseSvgDoc(svgContent);
  const seen = new Set<string>();
  const ids: string[] = [];
  const all = svgEl.querySelectorAll('[id]');
  for (const el of all) {
    const id = el.getAttribute('id');
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Return the direct geometry children of the root <svg> element — i.e. the
 * elements that `import("file.svg[N]")` can select by index. Metadata tags
 * (`defs`, `title`, `desc`, `metadata`, `style`) are excluded, matching the
 * same filtering used by splitSvg().
 *
 * @param svgContent  Raw SVG text content
 * @returns           Ordered list of top-level geometry elements
 */
export function getGeometryChildren(svgContent: string): Element[] {
  const { svgEl } = parseSvgDoc(svgContent);
  const children: Element[] = [];
  for (let i = 0; i < svgEl.children.length; i++) {
    const child = svgEl.children[i];
    const tag = child.tagName.toLowerCase().replace(/^svg:/, '');
    if (!SKIP_TAGS.has(tag)) {
      children.push(child);
    }
  }
  return children;
}

/**
 * Extract a single top-level geometry element by zero-based index from an SVG
 * and return it as a standalone SVG string.
 *
 * This implements `import("file.svg[N]")` — selecting elements by position for
 * SVGs that don't have id attributes.
 *
 * Metadata-only elements (`defs`, `title`, `desc`, `metadata`, `style`) are
 * skipped when counting, so index 0 always refers to the first visible shape.
 *
 * @param svgContent  Raw SVG text content
 * @param index       Zero-based index into the geometry children
 * @returns           A complete, standalone SVG string containing just that element
 * @throws            If the SVG cannot be parsed or the index is out of range
 */
export function extractSvgByIndex(svgContent: string, index: number): string {
  const { svgEl } = parseSvgDoc(svgContent);
  const rootAttrs = getRootAttributes(svgEl);

  const children: Element[] = [];
  for (let i = 0; i < svgEl.children.length; i++) {
    const child = svgEl.children[i];
    const tag = child.tagName.toLowerCase().replace(/^svg:/, '');
    if (!SKIP_TAGS.has(tag)) {
      children.push(child);
    }
  }

  if (children.length === 0) {
    throw new Error(
      `SVG has no geometry elements — cannot import by index`,
    );
  }
  if (index < 0 || index >= children.length) {
    throw new Error(
      `SVG index [${index}] is out of range — the SVG has ${children.length} geometry element(s) (indices 0–${children.length - 1})`,
    );
  }

  return buildSingleShapeSvg(rootAttrs, children[index]);
}

/**
 * Derive a virtual FS path for an SVG fragment file.
 *
 * Converts `import("file.svg#id1")` → `/__svg_frag__/file__id1.svg`
 * so the synthesized per-ID SVG lives in an isolated namespace, just like
 * `/__js_svg__/` files for js_svg() calls.
 *
 * @param svgFilename  The base SVG filename (e.g. "file.svg")
 * @param id           The fragment ID (e.g. "id1")
 */
export function svgFragmentVirtualPath(
  svgFilename: string,
  id: string,
): string {
  const base = getBaseName(svgFilename); // strips .svg, sanitises chars
  // Sanitise the ID for use in a filename (replace non-alphanumeric with _)
  const safeId = id.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
  return `/__svg_frag__/${base}__${safeId}.svg`;
}

/**
 * Derive a virtual FS path for an SVG index-import file.
 *
 * Converts `import("file.svg[2]")` → `/__svg_frag__/file__idx2.svg`
 *
 * @param svgFilename  The base SVG filename (e.g. "file.svg")
 * @param index        Zero-based element index (e.g. 2)
 */
export function svgIndexVirtualPath(
  svgFilename: string,
  index: number,
): string {
  const base = getBaseName(svgFilename);
  return `/__svg_frag__/${base}__idx${index}.svg`;
}

/**
 * Generate a ready-to-use OpenSCAD code snippet that imports all split SVG
 * files and arranges them side-by-side using a for loop.
 *
 * @param filenames  Ordered list of split SVG filenames
 * @param spacing    Horizontal spacing between shapes (default 10)
 */
export function generateOpenScadSnippet(
  filenames: string[],
  spacing = 10,
): string {
  if (filenames.length === 0) return '';

  const fileList = filenames.map((f) => `"${f}"`).join(', ');

  return `svg_files = [${fileList}];
for (i = [0 : len(svg_files) - 1]) {
    translate([i * ${spacing}, 0, 0])
        linear_extrude(height = 1)
            import(svg_files[i]);
}`;
}
